import type { ISODate } from "./dateUtils.ts";
import { todayUTC, toISODate } from "./dateUtils.ts";

export const DECK_TOPICS = {
  general: {
    name: "HR Summary Report",
  },
  attrition: {
    name: "Attrition",
  },
  recruitment: {
    name: "Recruitment",
  },
};

export type PortalGetDecksArgs = {
  pinned?: boolean;
  query?: string;
};

export type Deck = {
  id?: string;
  name: string;
  topic?: keyof typeof DECK_TOPICS;
  pinned: boolean;
  desired_slide_length: number;
  desired_timeframe_start: ISODate;
  desired_timeframe_end: ISODate;
  created_at: Date;
  updated_at: Date;
  slides: Slide[];
  user_id: string;
};

export type SlideStatus = "pending" | "processing" | "complete" | "error";

export type Slide = {
  id: string;
  report_id?: string;
  prompt?: string;
  title: string;
  speaker_notes: string;
  status: SlideStatus;
  error_message?: string;
};

export type DeckForDB = Omit<
  Deck,
  | "created_at"
  | "updated_at"
  | "desired_timeframe_start"
  | "desired_timeframe_end"
> & {
  user_id: string;
  created_at: string;
  updated_at: string;
  desired_timeframe_start: ISODate;
  desired_timeframe_end: ISODate;
};

export type DeckForCreateModal = Omit<Deck, "slides" | "user_id"> & {
  slides: string[];
};

export const makeDefaultDeck = (): DeckForCreateModal => {
  const now = new Date();
  const today = todayUTC();
  const oneYearAgo = today.subtract({ years: 1 }).toString() as ISODate;

  return {
    id: "",
    name: "Untitled Presentation",
    topic: undefined,
    pinned: false,
    desired_slide_length: 1,
    desired_timeframe_start: oneYearAgo,
    desired_timeframe_end: today.toString() as ISODate,
    created_at: now,
    updated_at: now,
    slides: [],
  };
};

export const hydrateDeck = (
  serverPayload: DeckForDB | DeckForCreateModal,
  user_id?: string,
): Deck => {
  const id = serverPayload["id"],
    name = serverPayload["name"],
    topic = serverPayload["topic"] as keyof typeof DECK_TOPICS,
    pinned = serverPayload["pinned"],
    desired_slide_length = serverPayload.desired_slide_length,
    slides = serverPayload.slides || [],
    desired_timeframe_start = toISODate(serverPayload.desired_timeframe_start),
    desired_timeframe_end = toISODate(serverPayload.desired_timeframe_end),
    created_at = new Date(serverPayload.created_at),
    updated_at = new Date(serverPayload.updated_at);

  const deck: Partial<Deck> = {
    id,
    name,
    topic,
    pinned,
    desired_slide_length,
    desired_timeframe_start,
    desired_timeframe_end,
    created_at,
    updated_at,
  };
  if ("user_id" in serverPayload) {
    // this is DeckForDB
    deck.user_id = user_id;
    deck.slides = slides as Slide[];
  } else {
    // this is DeckForCreateModal
    deck.user_id = user_id;
    deck.slides = [];
  }

  return deck as Deck;
};

export const slideFromReportId = (
  reportId: string,
  extraOpts?: object,
): Slide =>
  extraOpts
    ? {
        id: crypto.randomUUID(),
        report_id: reportId,
        title: "Untitled Slide",
        speaker_notes: "",
        status: "complete",
        ...extraOpts,
      }
    : {
        id: crypto.randomUUID(),
        report_id: reportId,
        title: "Untitled Slide",
        speaker_notes: "",
        status: "complete",
      };
